export type Role =
  | 'Processor'
  | 'Processor Assistant'
  | 'Loan Owner'
  | 'Team Lead'
  | 'Admin'

export type ViewType = 'My Pipeline' | 'Team Pipeline' | 'All In Process'

export type ModalType =
  | 'newApp'
  | 'sos'
  | 'rateLock'
  | 'admin'
  | 'export'
  | null

export interface Note {
  id: string
  text: string
  author: string
  timestamp: string
  /** "create" = SOS opened, "clear" = SOS cleared, "update" = follow-up */
  kind?: 'create' | 'clear' | 'update'
}

export interface Loan {
  id: string
  loanName: string
  isSos: boolean
  /** The most recent active SOS note (cleared on Clear SOS). */
  sosNote?: Note
  /** Full chronological history of SOS events for this loan. */
  sosNoteHistory: Note[]
  /** Recipients selected at SOS creation time. */
  sosRecipients?: string[]
  daysInPipe: number
  monthPushing: string
  stage: string
  processorNote: string
  loanOwner: string
  teamLead: string
  processor: string
  processorAssistant: string
  loanAmount: number
  lockExpiration: string
  scheduledClosing: string
  scheduledFunding: string
  isNewApp?: boolean
}

/** Today, for demo / determinism. */
export const TODAY = new Date('2026-05-19T00:00:00Z')

/**
 * Pipeline stage order — from CLOSEST to funding (top) to FURTHEST from funding (bottom).
 * Used for the default multi-sort. Lower index = closer to funding.
 */
export const STAGE_ORDER: string[] = [
  'Funded',
  'Closing',
  'Clear to Close',
  'Cond. Approved',
  'Underwriting',
  'Processing',
  'Setup',
  'Submitted',
  'Suspended',
]

export const ALL_MONTHS = [
  'May',
  'June',
  'July',
  'August',
  'September',
] as const

/**
 * Month order — current month first, then forward.
 * Months that have already passed sort to the end.
 */
export function monthOrder(month: string): number {
  const currentIdx = TODAY.getUTCMonth() // 0-based, May = 4
  const monthIdx = ALL_MONTHS.indexOf(month as (typeof ALL_MONTHS)[number])
  if (monthIdx === -1) return 999
  // Convert ALL_MONTHS index (which starts at May) to a real calendar month
  const calendarMonth = 4 + monthIdx // May=4 ... September=8
  // Relative distance from current month (negative = past)
  const diff = calendarMonth - currentIdx
  return diff < 0 ? 100 + Math.abs(diff) : diff
}

export function stageOrder(stage: string): number {
  const idx = STAGE_ORDER.indexOf(stage)
  return idx === -1 ? 999 : idx
}

export function isLockExpiredOrToday(lockExpiration: string): boolean {
  const lockDate = new Date(lockExpiration + 'T00:00:00Z')
  return lockDate.getTime() <= TODAY.getTime()
}

export function daysUntilLockExp(lockExpiration: string): number {
  const lockDate = new Date(lockExpiration + 'T00:00:00Z')
  return Math.ceil(
    (lockDate.getTime() - TODAY.getTime()) / (1000 * 60 * 60 * 24),
  )
}

/** Available recipients for SOS escalation. */
export const SOS_RECIPIENT_DIRECTORY: string[] = [
  'Jessica Pearson (Team Lead)',
  'Daniel Hardman (Team Lead)',
  'Louis Litt (Team Lead)',
  'Harvey Specter (Loan Owner)',
  'Mike Ross (Loan Owner)',
  'Katrina Bennett (Loan Owner)',
  'Operations Manager',
  'Compliance',
]

export type ColumnScope =
  | 'Processor'
  | 'Processor Assistant'
  | 'Loan Owner'
  | 'Team Lead — My Pipeline'
  | 'Team Lead — Team Pipeline'
  | 'Admin'

export const ALL_COLUMN_SCOPES: ColumnScope[] = [
  'Processor',
  'Processor Assistant',
  'Loan Owner',
  'Team Lead — My Pipeline',
  'Team Lead — Team Pipeline',
  'Admin',
]

/**
 * Default ordered column list per pipeline-view scope. SOS is always first.
 * Team Lead has two distinct scopes (My vs Team) so admins can curate them independently.
 */
export const SCOPE_COLUMNS: Record<ColumnScope, ColumnKey[]> = {
  Processor: [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'loanOwner',
    'teamLead',
    'processorAssistant',
  ],
  'Processor Assistant': [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'processor',
    'loanOwner',
    'teamLead',
  ],
  'Loan Owner': [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'processor',
    'teamLead',
    'loanAmount',
  ],
  'Team Lead — My Pipeline': [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'processor',
    'loanAmount',
  ],
  'Team Lead — Team Pipeline': [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'processor',
    'loanOwner',
    'loanAmount',
  ],
  Admin: [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'loanOwner',
    'teamLead',
    'processor',
    'processorAssistant',
    'loanAmount',
  ],
}

/** @deprecated — use SCOPE_COLUMNS keyed by ColumnScope. Kept for any legacy callers. */
export const ROLE_COLUMNS: Record<Role, ColumnKey[]> = {
  Processor: [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'loanOwner',
    'teamLead',
    'processorAssistant',
  ],
  'Processor Assistant': [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'processor',
    'loanOwner',
    'teamLead',
  ],
  'Loan Owner': [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'processor',
    'teamLead',
    'loanAmount',
  ],
  'Team Lead': [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'processor',
    'teamLead',
    'loanAmount',
  ],
  Admin: [
    'sos',
    'daysInPipe',
    'monthPushing',
    'loanName',
    'stage',
    'processorNote',
    'loanOwner',
    'teamLead',
    'processor',
    'processorAssistant',
    'loanAmount',
  ],
}

export type ColumnKey =
  | 'sos'
  | 'daysInPipe'
  | 'monthPushing'
  | 'loanName'
  | 'stage'
  | 'processorNote'
  | 'loanOwner'
  | 'teamLead'
  | 'processor'
  | 'processorAssistant'
  | 'loanAmount'
  | 'id'
  | 'lockExpiration'
  | 'scheduledClosing'
  | 'scheduledFunding'
  | 'isNewApp'
  | 'sosRecipients'

export const COLUMN_LABELS: Record<ColumnKey, string> = {
  sos: 'SOS',
  daysInPipe: 'Days In Pipe',
  monthPushing: 'Month Pushing',
  loanName: 'Loan Name',
  stage: 'Loan Stage',
  processorNote: 'Processor Note',
  loanOwner: 'Loan Owner',
  teamLead: 'Team Lead',
  processor: 'Loan Processor',
  processorAssistant: 'Processor Assistant',
  loanAmount: 'Total Loan Amount',
  id: 'Loan ID',
  lockExpiration: 'Lock Expiration Date',
  scheduledClosing: 'Scheduled Closing Date',
  scheduledFunding: 'Scheduled Funding Date',
  isNewApp: 'New Application',
  sosRecipients: 'SOS Recipients',
}

/** Optional short description shown in the column picker. */
export const COLUMN_DESCRIPTIONS: Partial<Record<ColumnKey, string>> = {
  sos: 'SOS flag indicator',
  daysInPipe: 'Days the loan has been in the pipeline',
  monthPushing: 'Target funding month',
  loanName: 'Display name of the loan',
  stage: 'Current pipeline stage',
  processorNote: 'Latest processor note',
  loanOwner: 'Responsible loan officer',
  teamLead: 'Responsible team lead',
  processor: 'Assigned loan processor',
  processorAssistant: 'Assigned processor assistant',
  loanAmount: 'Total loan dollar amount',
  id: 'Internal loan record ID',
  lockExpiration: 'Date the rate lock expires',
  scheduledClosing: 'Scheduled closing date',
  scheduledFunding: 'Scheduled funding date',
  isNewApp: 'Marker for newly received applications',
  sosRecipients: 'People notified when SOS is flagged',
}

export const MOCK_LOANS: Loan[] = [
  {
    id: 'L-1001',
    loanName: 'Smith Refinance',
    isSos: true,
    sosNote: {
      id: 'n1',
      text: 'Appraisal came in low, need immediate review for restructure.',
      author: 'Sarah Jenkins',
      timestamp: '2026-05-19T09:15:00Z',
      kind: 'create',
    },
    sosNoteHistory: [
      {
        id: 'n1',
        text: 'Appraisal came in low, need immediate review for restructure.',
        author: 'Sarah Jenkins',
        timestamp: '2026-05-19T09:15:00Z',
        kind: 'create',
      },
    ],
    sosRecipients: ['Jessica Pearson (Team Lead)', 'Operations Manager'],
    daysInPipe: 32,
    monthPushing: 'May',
    stage: 'Underwriting',
    processorNote: 'Waiting on updated HOI.',
    loanOwner: 'Mike Ross',
    teamLead: 'Jessica Pearson',
    processor: 'Alex Chen',
    processorAssistant: 'Sam Smith',
    loanAmount: 450000,
    lockExpiration: '2026-05-25',
    scheduledClosing: '2026-05-28',
    scheduledFunding: '2026-05-30',
  },
  {
    id: 'L-1002',
    loanName: 'Johnson Purchase',
    isSos: false,
    sosNoteHistory: [],
    daysInPipe: 12,
    monthPushing: 'June',
    stage: 'Processing',
    processorNote: 'All initial docs received. Submitting to UW today.',
    loanOwner: 'Harvey Specter',
    teamLead: 'Jessica Pearson',
    processor: 'Alex Chen',
    processorAssistant: 'Sam Smith',
    loanAmount: 820000,
    lockExpiration: '2026-06-15',
    scheduledClosing: '2026-06-10',
    scheduledFunding: '2026-06-12',
    isNewApp: true,
  },
  {
    id: 'L-1003',
    loanName: 'Williams HELOC',
    isSos: false,
    sosNoteHistory: [],
    daysInPipe: 45,
    monthPushing: 'May',
    stage: 'Clear to Close',
    processorNote: 'CTC issued. Scheduling closing.',
    loanOwner: 'Louis Litt',
    teamLead: 'Daniel Hardman',
    processor: 'Rachel Zane',
    processorAssistant: 'Donna Paulsen',
    loanAmount: 150000,
    // Lock expires TODAY — follows expired-lock SOS logic
    lockExpiration: '2026-05-19',
    scheduledClosing: '2026-05-22',
    scheduledFunding: '2026-05-24',
  },
  {
    id: 'L-1004',
    loanName: 'Garcia FHA',
    isSos: true,
    sosNote: {
      id: 'n2',
      text: 'Borrower lost job, need to cancel or find co-borrower ASAP.',
      author: 'Mike Ross',
      timestamp: '2026-05-18T14:30:00Z',
      kind: 'create',
    },
    sosNoteHistory: [
      {
        id: 'n2',
        text: 'Borrower lost job, need to cancel or find co-borrower ASAP.',
        author: 'Mike Ross',
        timestamp: '2026-05-18T14:30:00Z',
        kind: 'create',
      },
    ],
    sosRecipients: ['Jessica Pearson (Team Lead)'],
    daysInPipe: 28,
    monthPushing: 'June',
    stage: 'Suspended',
    processorNote: 'File on hold pending employment update.',
    loanOwner: 'Mike Ross',
    teamLead: 'Jessica Pearson',
    processor: 'Alex Chen',
    processorAssistant: 'Sam Smith',
    loanAmount: 310000,
    lockExpiration: '2026-06-05',
    scheduledClosing: '2026-06-01',
    scheduledFunding: '2026-06-03',
  },
  {
    id: 'L-1005',
    loanName: 'Chen VA Loan',
    isSos: false,
    sosNoteHistory: [],
    daysInPipe: 8,
    monthPushing: 'July',
    stage: 'Setup',
    processorNote: 'Ordering VA appraisal.',
    loanOwner: 'Katrina Bennett',
    teamLead: 'Louis Litt',
    processor: 'Rachel Zane',
    processorAssistant: 'Donna Paulsen',
    loanAmount: 550000,
    lockExpiration: '2026-07-10',
    scheduledClosing: '2026-07-05',
    scheduledFunding: '2026-07-08',
    isNewApp: true,
  },
  {
    id: 'L-1006',
    loanName: 'Patel Jumbo',
    isSos: false,
    sosNoteHistory: [],
    daysInPipe: 18,
    monthPushing: 'June',
    stage: 'Underwriting',
    processorNote: 'Conditions uploaded, waiting on UW review.',
    loanOwner: 'Harvey Specter',
    teamLead: 'Jessica Pearson',
    processor: 'Alex Chen',
    processorAssistant: 'Sam Smith',
    loanAmount: 1250000,
    lockExpiration: '2026-06-20',
    scheduledClosing: '2026-06-18',
    scheduledFunding: '2026-06-20',
  },
  {
    id: 'L-1007',
    loanName: 'Anderson Refi',
    isSos: false,
    sosNoteHistory: [],
    daysInPipe: 22,
    monthPushing: 'June',
    stage: 'Processing',
    processorNote: 'Pearson personally owns — fast-track for VIP borrower.',
    loanOwner: 'Jessica Pearson',
    teamLead: 'Jessica Pearson',
    processor: 'Rachel Zane',
    processorAssistant: 'Donna Paulsen',
    loanAmount: 675000,
    lockExpiration: '2026-06-25',
    scheduledClosing: '2026-06-20',
    scheduledFunding: '2026-06-22',
  },
  {
    id: 'L-1008',
    loanName: 'Brooks Construction',
    isSos: false,
    sosNoteHistory: [],
    daysInPipe: 5,
    monthPushing: 'July',
    stage: 'Setup',
    processorNote: 'Initial intake. Awaiting docs from borrower.',
    loanOwner: 'Jessica Pearson',
    teamLead: 'Jessica Pearson',
    processor: 'Alex Chen',
    processorAssistant: 'Sam Smith',
    loanAmount: 980000,
    lockExpiration: '2026-07-15',
    scheduledClosing: '2026-07-12',
    scheduledFunding: '2026-07-14',
    isNewApp: true,
  },
]
